import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, Heart, TrendingDown } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { mediaUrl, IMG } from '../../lib/mediaUrl';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// Saved products. Shows current price + stock and, if the price has dropped
// since it was saved, a little "down $X" flag.
//
// NOTE (stub): the actual "email me when this drops / restocks" send is not
// wired yet — it needs the Resend channel (see PLAN.md). The notify_on_drop /
// notify_on_restock flags and the price_cents_at_add baseline are already
// stored per row, so that layer plugs in later without schema or UI churn.
export default function WishlistTab() {
  const { user } = useAuth();
  const { remove } = useWishlist();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('wishlist_items')
      .select('id, product_id, price_cents_at_add, created_at, products(name, slug, price_cents, quantity_available, status, product_media(storage_path, sort_order))')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setRows((data || []).filter((r) => r.products)); setLoading(false); });
  }, [user]);

  const coverUrl = (product) => {
    const photos = (product.product_media || []).slice().sort((a, b) => a.sort_order - b.sort_order);
    return photos[0] ? mediaUrl(photos[0].storage_path, IMG.thumb) : null;
  };

  const handleRemove = async (productId) => {
    await remove(productId);
    setRows((prev) => prev.filter((r) => r.product_id !== productId));
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;

  if (rows.length === 0) {
    return (
      <div>
        <p style={{ color: 'var(--text-muted)', marginBottom: 6 }}>Your wishlist is empty.</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 14 }}>
          Tap the heart on any item to save it here.
        </p>
        <Link to="/shop" className="sv-btn" style={{ display: 'inline-flex' }}>Browse the shop</Link>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 6px' }}>
        We'll be able to email you on price drops and restocks soon.
      </p>
      {rows.map((r) => {
        const p = r.products;
        const photoUrl = coverUrl(p);
        const soldOut = p.quantity_available <= 0 || p.status !== 'active';
        const dropped = r.price_cents_at_add != null && p.price_cents < r.price_cents_at_add;
        return (
          <div key={r.id} className="sv-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12 }}>
            <Link
              to={`/product/${p.slug}`}
              style={{
                width: 56, height: 56, flexShrink: 0, borderRadius: 8, overflow: 'hidden',
                background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              {photoUrl ? (
                <img src={photoUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
              ) : (
                <ImageOff size={20} />
              )}
            </Link>

            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={`/product/${p.slug}`} style={{ fontWeight: 700, fontSize: '0.92rem', display: 'block' }}>
                {p.name}
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                <span style={{ fontWeight: 800 }}>{money(p.price_cents)}</span>
                {dropped && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', color: 'var(--success)', fontWeight: 700 }}>
                    <TrendingDown size={13} /> down {money(r.price_cents_at_add - p.price_cents)}
                  </span>
                )}
                {soldOut && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--danger)', fontWeight: 700 }}>SOLD OUT</span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleRemove(r.product_id)}
              aria-label="Remove from wishlist"
              className="sv-btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.8rem' }}
            >
              <Heart size={14} fill="var(--accent)" color="var(--accent)" /> Remove
            </button>
          </div>
        );
      })}
    </div>
  );
}
